import React from "react";
import { Download02Icon, UserCircleIcon } from "hugeicons-react";
import tataPlayStackedLogo from "../assets/tata_play_stacked_logo.png";
import type { ChatMessageProps } from "../types";

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  userName,
}) => {
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseMarkdown = (text: string) => {
    if (!text) return text;

    let html = text;

    // Tables: Parse markdown tables
    const tableRegex = /((?:^\|.+\|\s*$\n?)+)/gm;
    html = html.replace(tableRegex, (match) => {
      const lines = match.trim().split("\n");
      if (lines.length < 2) return match;

      // Check if second line is separator (contains dashes)
      if (!lines[1].match(/^\|[\s\-:|]+\|$/)) return match;

      const headers = lines[0]
        .split("|")
        .slice(1, -1)
        .map((h) => h.trim());
      const rows = lines.slice(2).map((line) =>
        line
          .split("|")
          .slice(1, -1)
          .map((cell) => cell.trim()),
      );

      let table = "<table><thead><tr>";
      headers.forEach((h) => {
        table += `<th>${h}</th>`;
      });
      table += "</tr></thead><tbody>";
      rows.forEach((row) => {
        table += "<tr>";
        row.forEach((cell) => {
          table += `<td>${cell}</td>`;
        });
        table += "</tr>";
      });
      table += "</tbody></table>";

      return '<div class="table-wrapper">' + table + "</div>";
    });

    // Headers
    html = html.replace(/^### (.*$)/gm, "<h3>$1</h3>");
    html = html.replace(/^## (.*$)/gm, "<h2>$1</h2>");
    html = html.replace(/^# (.*$)/gm, "<h1>$1</h1>");

    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/__(.+?)__/g, "<strong>$1</strong>");

    // Italic: *text* or _text_
    html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
    html = html.replace(/_(.+?)_/g, "<em>$1</em>");

    // Code: `code`
    html = html.replace(/`(.+?)`/g, "<code>$1</code>");

    // Line breaks
    html = html.replace(/\n/g, "<br />");

    // Bullet points: - item or * item
    html = html.replace(/^[\-\*]\s(.+)$/gm, "<li>$1</li>");
    html = html.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

    return html;
  };

  const renderText = (text: string) => {
    const parsed = parseMarkdown(text);
    return <span dangerouslySetInnerHTML={{ __html: parsed }} />;
  };

  return (
    <div
      className={`message ${message.sender === "user" ? "message-user" : "message-bot"}`}
    >
      <div
        className="message-avatar"
        style={{
          backgroundColor: "#ffff",
          width: "50px",
          height: "50px",
          borderRadius: "50%",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {message.sender === "user" ? (
          // <UserIcon size={30} color="rgba(0, 0, 0, 0.9)" />
          <UserCircleIcon size={50} color="rgba(0, 0, 0, 0.9)" />
        ) : (
          <img
            src={tataPlayStackedLogo}
            alt="Bot"
            style={{
              width: "60px",
              height: "52px",
              padding: "6px",
            }}
          />
        )}
      </div>
      <div className="message-content">
        <div className="message-header">
          {message.sender === "user" && (
            <span className="message-sender">{userName}</span>
          )}
          <span className="message-time">{formatTime(message.timestamp)}</span>
        </div>
        {message.isGenerating ? (
          <div className="message-generating">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span className="generating-text">{message.text}</span>
          </div>
        ) : message.isFile && message.fileUrl ? (
          <div className="message-file">
            <a
              href={message.fileUrl}
              download
              className="file-download-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download02Icon size={20} />
              <span>{message.text}</span>
            </a>
          </div>
        ) : (
          <div className="message-text">{renderText(message.text)}</div>
        )}
      </div>
    </div>
  );
};
